<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { call } from '../api/http'
import { ledger, listTransactions, type Account, type Transaction, type TxFilter } from '../api/ledger'
import { useSession } from '../stores/session'
import { useForm } from '../lib/form'
import { formatMoney } from '../lib/money'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import EmptyState from '../components/EmptyState.vue'

const { locale } = useI18n()
const session = useSession()
const load = useForm()
const action = useForm()

const accounts = ref<Account[]>([])
const categories = ref<string[]>([])
const items = ref<Transaction[]>([])
const cursor = ref<string | undefined>()
const route = useRoute()
const qs = (k: string) => (typeof route.query[k] === 'string' ? (route.query[k] as string) : '')
const filter = reactive<TxFilter>({ account_id: qs('account_id'), from: '', to: '', q: '', category: qs('category'), direction: '' })

const accountName = computed(() => Object.fromEntries(accounts.value.map((a) => [a.id, a.name])))

async function loadMeta() {
  const [a, c] = await Promise.all([call(ledger.GET('/accounts')), call(ledger.GET('/categories'))])
  accounts.value = a
  categories.value = c.map((x) => x.code)
}

/** First page for the current filter (append=false) or the next page (append=true). */
async function fetchPage(append: boolean) {
  await load.submit(async () => {
    const page = await listTransactions(filter, append ? cursor.value : undefined)
    items.value = append ? [...items.value, ...page.items] : page.items
    cursor.value = page.next_cursor
  })
}

watch(
  () => session.workspace?.id,
  async () => (await load.submit(loadMeta)) && fetchPage(false),
  { immediate: true },
)
watch(filter, () => fetchPage(false))

async function setCategory(tx: Transaction, e: Event) {
  const category = (e.target as HTMLInputElement).value.trim().toUpperCase()
  if (category === (tx.category ?? '')) return
  await action.submit(async () => {
    Object.assign(tx, await call(ledger.PATCH('/transactions/{id}', { params: { path: { id: tx.id } }, body: { category } })))
    if (category && !categories.value.includes(category)) categories.value = [...categories.value, category]
  })
}

const counterparty = (tx: Transaction) => tx.counterparty_name || tx.counterparty_iban || '—'
</script>

<template>
  <div class="page wide">
    <header class="page-header">
      <div>
        <h1>{{ $t('transactions.title') }}</h1>
        <p class="muted">{{ $t('transactions.subtitle') }}</p>
      </div>
    </header>

    <UiCard>
      <form class="filters" data-testid="tx-filters" @submit.prevent>
        <select v-model="filter.account_id" :aria-label="$t('transactions.account')">
          <option value="">{{ $t('transactions.allAccounts') }}</option>
          <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
        </select>
        <input v-model="filter.from" type="date" :aria-label="$t('transactions.from')" :title="$t('transactions.from')" />
        <input v-model="filter.to" type="date" :aria-label="$t('transactions.to')" :title="$t('transactions.to')" />
        <input v-model.lazy.trim="filter.q" type="search" :placeholder="$t('transactions.search')" :aria-label="$t('transactions.search')" />
        <select v-model="filter.category" :aria-label="$t('transactions.category')">
          <option value="">{{ $t('transactions.allCategories') }}</option>
          <option value="none">{{ $t('transactions.uncategorised') }}</option>
          <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
        </select>
        <select v-model="filter.direction" :aria-label="$t('transactions.direction')">
          <option value="">{{ $t('transactions.allDirections') }}</option>
          <option value="in">{{ $t('transactions.in') }}</option>
          <option value="out">{{ $t('transactions.out') }}</option>
        </select>
      </form>
    </UiCard>

    <ErrorBanner :error="action.error.value ?? load.error.value" />

    <UiCard>
      <p v-if="load.busy.value && !items.length" class="muted">{{ $t('common.loading') }}</p>
      <EmptyState v-else-if="!items.length" :title="$t('transactions.empty')" :message="$t('transactions.emptyText')">
        <RouterLink v-if="session.canWrite" to="/import">{{ $t('nav.import') }}</RouterLink>
      </EmptyState>
      <table v-else class="tx" data-testid="tx-table">
        <thead>
          <tr>
            <th>{{ $t('transactions.date') }}</th>
            <th>{{ $t('transactions.details') }}</th>
            <th>{{ $t('transactions.category') }}</th>
            <th class="num">{{ $t('transactions.amount') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tx in items" :key="tx.id" data-testid="tx-row">
            <td class="date">{{ tx.booked_at }}</td>
            <td class="details">
              <strong>{{ counterparty(tx) }}</strong>
              <span v-if="tx.remittance" class="muted small">{{ tx.remittance }}</span>
              <span class="muted small">{{ accountName[tx.account_id] }}</span>
            </td>
            <td class="cat">
              <input
                v-if="session.canWrite"
                :value="tx.category ?? ''"
                list="tx-categories"
                maxlength="32"
                :placeholder="$t('transactions.uncategorised')"
                :aria-label="$t('transactions.category')"
                :disabled="action.busy.value"
                @change="setCategory(tx, $event)"
              />
              <span v-else>{{ tx.category ?? '—' }}</span>
            </td>
            <td :class="['num', tx.amount.amount_minor < 0 ? 'out' : 'in']">{{ formatMoney(tx.amount, locale, true) }}</td>
          </tr>
        </tbody>
      </table>
      <datalist id="tx-categories">
        <option v-for="c in categories" :key="c" :value="c" />
      </datalist>
      <div v-if="cursor" class="more">
        <UiButton variant="outline" :loading="load.busy.value" @click="fetchPage(true)">{{ $t('transactions.loadMore') }}</UiButton>
      </div>
    </UiCard>
  </div>
</template>

<style scoped>
.wide {
  max-width: 1200px;
}
.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
}
.tx {
  width: 100%;
  border-collapse: collapse;
}
.tx th {
  text-align: left;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--fg-muted);
  padding: 0 0.5rem 0.5rem;
}
.tx td {
  padding: 0.6rem 0.5rem;
  border-top: 1px solid var(--border);
  vertical-align: top;
}
.date {
  white-space: nowrap;
}
.details {
  display: flex;
  flex-direction: column;
  word-break: break-word;
}
.cat {
  width: 10rem;
}
.cat input {
  min-height: 2rem;
  padding: 0.2rem 0.5rem;
}
.num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.in {
  color: var(--gain);
}
.out {
  color: var(--loss);
}
.more {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

/* Mobile: each row becomes a card. */
@media (max-width: 640px) {
  .tx thead {
    display: none;
  }
  .tx tr {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.25rem 0.75rem;
    padding: 0.75rem 0;
    border-top: 1px solid var(--border);
  }
  .tx td {
    border: 0;
    padding: 0;
  }
  .date {
    grid-column: 1;
    font-size: 0.85rem;
    color: var(--fg-muted);
  }
  .num {
    grid-column: 2;
    grid-row: 1;
  }
  .details,
  .cat {
    grid-column: 1 / -1;
    width: auto;
  }
}
</style>
